import React, { FC } from 'react'

import { IconAsset } from '@avantstay/backoffice-vectors'

import { isImage as checkIsImage } from '../../../../utils'
import { FilePreviewType, PreviewProps, UploadAreaType } from '../../UploadArea.types'
import { colorsByExtension } from './FilePreview.constants'
import * as S from './FilePreview.styles'

const getFileExtension = (file: PreviewProps['file']): string =>
  (((file.fileName || file.url) ?? '').split('.').pop() ?? '').toUpperCase()

export const Preview: FC<PreviewProps> = ({
  file,
  isImageCover,
  hasLowResolution,
  isFileEnabled,
  filesWithError,
  filePreviewType,
  uploadAreaType,
  onMakeAsCover,
  onOpenImagePreview,
}) => {
  const extension = getFileExtension(file)
  const isDocument = uploadAreaType === UploadAreaType.document
  const isImageFile = checkIsImage(extension)

  const isPhotoGallery = filePreviewType === FilePreviewType.photoGallery

  const fileCurrentId = isDocument ? file.documentId : file.id

  /**
   * We pass files with errors with the original File object
   * in order to be able to retry to upload the file
   */
  const originalFile = filesWithError && fileCurrentId && filesWithError[fileCurrentId]
  const hasError = !!originalFile

  if (isDocument) {
    return (
      <S.DocumentPlaceholder>
        <S.DocumentPreview src={file.url} />
      </S.DocumentPlaceholder>
    )
  }

  if (isImageFile) {
    return (
      <S.ImagePlaceholder
        error={hasError}
        warning={hasLowResolution}
        $isCover={isImageCover}
        $hasFlatBorder={isPhotoGallery}
        $isDisabled={isPhotoGallery && !isFileEnabled}
      >
        {hasLowResolution ? (
          <S.ImageOverlay
            warning={hasLowResolution}
            error={hasError}
            onClick={() => onOpenImagePreview(true)}
          />
        ) : null}
        <S.Image
          src={file.url}
          onClick={() => onOpenImagePreview(true)}
          $hasFlatBorder={isPhotoGallery}
        />
        {isPhotoGallery ? (
          isImageCover ? (
            <S.Cover>Gallery Cover</S.Cover>
          ) : (
            onMakeAsCover && (
              <S.MakeAsCoverButton onClick={onMakeAsCover}>Use as cover</S.MakeAsCoverButton>
            )
          )
        ) : null}
      </S.ImagePlaceholder>
    )
  }

  return (
    <S.FileContainer>
      <IconAsset />
      <S.Extension
        background={colorsByExtension[extension]?.background}
        color={colorsByExtension[extension]?.background}
      >
        {extension}
      </S.Extension>
    </S.FileContainer>
  )
}
